import { zodResolver } from '@hookform/resolvers/zod';
import { Paperclip, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FormSection } from '@/components/ui/form-section';
import { InfoHint } from '@/components/ui/info-hint';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import type { ApiError } from '@/core/api';
import { TEMPLATE_TYPES } from '../constants';
import type { Template, TemplateInput } from '../types';

const schema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(255),
  type: z.enum([
    'agreement', 'sow', 'nda', 'offer', 'proposal', 'cv', 'email', 'project_materials', 'other',
  ]),
  body: z.string(),
  is_active: z.boolean(),
});

type FormValues = z.infer<typeof schema>;
const FIELD_KEYS = ['name', 'type', 'body', 'file', 'is_active'] as const;

/** Pull a display name out of a file URL (no extra deps). */
function filenameFromUrl(url: string): string {
  try {
    const path = new URL(url, window.location.origin).pathname;
    return decodeURIComponent(path.split('/').pop() || url);
  } catch {
    return url;
  }
}

interface TemplateFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template?: Template;
  onSubmit: (input: TemplateInput) => Promise<unknown>;
}

function toValues(template?: Template): FormValues {
  return {
    name: template?.name ?? '',
    type: template?.type ?? 'other',
    body: template?.body ?? '',
    is_active: template?.is_active ?? true,
  };
}

export function TemplateFormDialog({ open, onOpenChange, template, onSubmit }: TemplateFormDialogProps) {
  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: toValues(template) });
  /** `file` lives outside react-hook-form because it's a `File` (not JSON-serializable).
   *  States: `undefined` = leave server-side file unchanged · `File` = upload new ·
   *  `null` = clear existing file on save. */
  const [pickedFile, setPickedFile] = useState<File | null | undefined>(undefined);

  const { reset } = form;
  useEffect(() => {
    if (open) reset(toValues(template));
  }, [open, template, reset]);

  /** Wrap the parent's `onOpenChange` so closing resets the file picker too.
   *  The native `<input type=file>` element remounts via the `key` below, so
   *  no DOM ref/manipulation is needed — the picked value is purely React state. */
  function handleOpenChange(next: boolean) {
    if (!next) setPickedFile(undefined);
    onOpenChange(next);
  }
  // Remounts the file input on each fresh open so a stale selection from the
  // previous open can't bleed through.
  const fileInputKey = `${open ? 'open' : 'closed'}-${template?.id ?? 'new'}`;

  async function handleSubmit(values: FormValues) {
    try {
      const payload: TemplateInput = { ...values };
      if (pickedFile !== undefined) payload.file = pickedFile;
      await onSubmit(payload);
      handleOpenChange(false);
    } catch (e) {
      const err = e as ApiError;
      if (err?.fieldErrors) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          const key = FIELD_KEYS.find((k) => k === field);
          form.setError(key === 'file' ? 'root' : (key ?? 'root'), {
            message: messages.join(' '),
          });
        }
      }
    }
  }

  const currentFileUrl = template?.file ?? null;
  const showExisting = pickedFile === undefined && currentFileUrl !== null;
  const showPicked = pickedFile instanceof File;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{template ? 'Edit template' : 'New template'}</DialogTitle>
          <DialogDescription>
            Use <code className="text-xs">{'{{ merge_fields }}'}</code> for values filled in at render time.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            <FormSection title="Template">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Mutual NDA" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Type</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {TEMPLATE_TYPES.map((t) => (
                            <SelectItem key={t.value} value={t.value}>
                              {t.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="is_active"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Active</FormLabel>
                      <FormControl>
                        {/* Match the Type select's visual envelope: same height,
                            same border, same dark bg — so the row aligns. */}
                        <div className="border-input flex h-9 items-center justify-between rounded-md border bg-transparent px-3 shadow-xs dark:bg-input/30">
                          <span className="text-muted-foreground text-sm">
                            {field.value ? 'Visible to users' : 'Hidden — drafts only'}
                          </span>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            aria-label="Active"
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Wave 15 — optional source file (e.g. .docx / .pdf). */}
              <FormItem>
                <FormLabel htmlFor="template-file" className="flex items-center gap-1.5">
                  Source file (optional)
                  <InfoHint text="Attach a source document (e.g. .docx / .pdf). Optional — the body below is rendered into a new Document on its own." />
                </FormLabel>
                <FormControl>
                  <div className="border-input flex flex-wrap items-center gap-3 rounded-md border bg-transparent p-2 shadow-xs dark:bg-input/30">
                    <input
                      key={fileInputKey}
                      id="template-file"
                      type="file"
                      className="text-sm file:mr-3 file:inline-flex file:h-7 file:cursor-pointer file:rounded-md file:border-0 file:bg-primary file:px-3 file:text-sm file:font-medium file:text-primary-foreground hover:file:opacity-90"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        setPickedFile(f ?? undefined);
                      }}
                    />
                    {showPicked ? (
                      <span className="text-muted-foreground inline-flex items-center gap-1 text-xs">
                        <Paperclip className="size-3" />
                        {(pickedFile as File).name}
                      </span>
                    ) : showExisting ? (
                      <a
                        href={currentFileUrl as string}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary inline-flex items-center gap-1 text-xs underline"
                      >
                        <Paperclip className="size-3" />
                        {filenameFromUrl(currentFileUrl as string)}
                      </a>
                    ) : null}
                    {(showPicked || showExisting) ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="ml-auto h-7 px-2"
                        onClick={() => {
                          // Toggle between "clear existing on save" and "drop the new pick".
                          setPickedFile(showExisting ? null : undefined);
                        }}
                      >
                        <X className="size-3" />
                        {showExisting ? 'Remove' : 'Clear'}
                      </Button>
                    ) : null}
                  </div>
                </FormControl>
              </FormItem>
            </FormSection>

            <FormSection title="Body">
              <FormField
                control={form.control}
                name="body"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-1.5">
                      Body
                      <InfoHint text="Editing the body does not change documents already rendered from it." />
                    </FormLabel>
                    <FormControl>
                      <Textarea rows={10} className="font-mono text-sm" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormSection>

            {form.formState.errors.root ? (
              <p className="text-destructive text-sm">{form.formState.errors.root.message}</p>
            ) : null}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? 'Saving…' : template ? 'Save changes' : 'Create template'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
